import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '@platewell/schema';
import { useState, type FormEvent } from 'react';
import { changePassword } from '../../lib/api.js';

type Tone = 'weak' | 'fair' | 'strong';

/**
 * An advisory score, not a gate.
 *
 * The server's minimum length is the only rule that actually refuses anything; this exists
 * so a password that merely clears the minimum does not feel like a good one.
 */
function score(password: string): number {
  if (password === '') return 0;
  return Math.min(
    4,
    (password.length >= MIN_PASSWORD_LENGTH ? 1 : 0) +
      (password.length >= 14 ? 1 : 0) +
      (/[^a-z]/.test(password) ? 1 : 0) +
      (/[^\w]/.test(password) ? 1 : 0),
  );
}

const LABELS = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'];

function toneFor(value: number): Tone {
  if (value >= 3) return 'strong';
  if (value === 2) return 'fair';
  return 'weak';
}

export function PasswordForm({ providers }: { providers: string[] }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [revokeOthers, setRevokeOthers] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const strength = score(next);
  const tone = toneFor(strength);
  const mismatch = confirm !== '' && confirm !== next;
  const canSubmit =
    current !== '' && next.length >= MIN_PASSWORD_LENGTH && confirm === next && !busy;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (next.length < MIN_PASSWORD_LENGTH || next.length > MAX_PASSWORD_LENGTH) {
      setError(`Password must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (next !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await changePassword(current, next, revokeOthers);
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (cause) {
      // The fields are deliberately left filled: the usual failure is a mistyped current
      // password, and clearing three inputs to fix one is a punishment.
      setError(cause instanceof Error ? cause.message : 'Could not change your password');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} style={{ maxWidth: 420 }}>
      <div className="field" style={{ marginBottom: 12 }}>
        <label className="label" htmlFor="current-password">
          Current password
        </label>
        <input
          id="current-password"
          className="input"
          type="password"
          required
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
      </div>

      <div className="field" style={{ marginBottom: 8 }}>
        <label className="label" htmlFor="new-password">
          New password
        </label>
        <input
          id="new-password"
          className="input"
          type="password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          maxLength={MAX_PASSWORD_LENGTH}
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
        />
      </div>

      <div
        className="strength"
        data-tone={next === '' ? undefined : tone}
        style={{ marginBottom: 12 }}
      >
        <span className="strength-track">
          <span className="strength-fill" style={{ ['--strength' as string]: `${(strength / 4) * 100}%` }} />
        </span>
        <span className="strength-label">
          {next === '' ? `At least ${MIN_PASSWORD_LENGTH} characters` : LABELS[strength]}
        </span>
      </div>

      <div className="field" style={{ marginBottom: 12 }}>
        <label className="label" htmlFor="confirm-new-password">
          Confirm new password
        </label>
        <input
          id="confirm-new-password"
          className="input"
          type="password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          maxLength={MAX_PASSWORD_LENGTH}
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />
        {mismatch && <p className="error-text">Those two passwords do not match.</p>}
      </div>

      <label className="form-checkbox" style={{ marginBottom: 14 }}>
        <input
          type="checkbox"
          checked={revokeOthers}
          onChange={(event) => setRevokeOthers(event.target.checked)}
        />
        <span>
          Sign out my other devices. They keep the recipes already on them, but will need
          this password to sync again.
        </span>
      </label>

      <div className="row wrap">
        <button className="btn primary" type="submit" disabled={!canSubmit}>
          {busy ? 'Saving…' : 'Update password'}
        </button>
        {done && <span className="ok-text">Password updated.</span>}
      </div>

      {error != null && <p className="error-text">{error}</p>}
      {providers.length > 1 && (
        <p className="hint">You can also keep signing in with {socialLabel(providers)}.</p>
      )}
    </form>
  );
}

export function socialLabel(providers: string[]): string {
  const names = providers
    .filter((provider) => provider !== 'credential')
    .map((provider) => (provider === 'google' ? 'Google' : provider === 'apple' ? 'Apple' : provider));
  if (names.length === 0) return 'another provider';
  if (names.length === 1) return names[0] as string;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
